import { Dialog, Box, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ContactRow } from "../../../../data/Contact/models";
import { newDemandWithTypeFields } from "../../../../data/Incident/forms/dialog";
import {
  IncidentParentRow,
  IncidentPost,
} from "../../../../data/Incident/models";
import { createIncident } from "../../../../data/Incident/queries";
import { ServiceRow } from "../../../../data/Service/models";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import useUser from "../../../../hooks/useUserData";
import { RouterRoutes } from "../../../../routes/routes";
import { QueryError } from "../../../../types";
import Transition from "../../../common/animation/DialogTransition";
import SimpleFormWithSectionsWithState from "../../../form/SimpleForm/SimpleFormWithSectionsWithState";
import { buildPartialFormObj } from "../../../form/SimpleForm/buildForm";
import {
  ClosedAccountPrograms,
  ProgramRow,
} from "../../../../data/Program/models";
import { routes } from "../../../../routes/routes";
import { getPrograms } from "../../../../data/Program/queries";

type Props = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  demand: IncidentParentRow;
  contacts: ContactRow[];
  programs: ProgramRow[];
};

const NewDemandDialog = ({
  open,
  setOpen,
  demand,
  contacts,
  programs,
}: Props) => {
  const queryClient = useQueryClient();
  const { userData, userHasRole } = useUser();

  const [fields, setFields] = useState(
    buildPartialFormObj(newDemandWithTypeFields, {
      title: { value: demand.title },
      vdl_contactprincipal: {
        value: userData.contactid,
        choices: (userHasRole("Administrateur")
          ? contacts.filter((e) => e.statecode === 0)
          : contacts.filter(
              (e) => e.statecode === 0 && e.contactid === userData.contactid
            )
        ).map((contact) => {
          return {
            label: `${contact.firstname} ${contact.lastname}${
              contact.contactid === userData.contactid ? " (vous)" : ""
            }`,
            value: contact.contactid,
          };
        }),
      },
      vdl_typededemande: {
        value: null,
        choices: programs.flatMap((program) =>
          program.services
            .filter((service) => service.vdl_demandeenfantpermis)
            .map((service) => ({
              value: service.vdl_typedeserviceid,
              label: service.vdl_name,
            }))
        ),
      },
    })
  );

  const mutation = useMutation<string | undefined, QueryError, IncidentPost>({
    mutationFn: (incident) => createIncident(incident),
    onSuccess: (data?: string) => {
      if (data) {
        showSnackbar("Demande créée", "success");
        queryClient.invalidateQueries({
          queryKey: ["Demand", demand.incidentid],
        });
        // redirection vers la demande créée en edit
        navigate(`${routes.demands.path}/${data}/modifier?tab=1`);
        setOpen(false);
      }
    },

    onError: (error: QueryError) => {
      showErrorSnackbar(
        "Erreur dans la création de la demande.",
        "error",
        error
      );
      setOpen(false);
    },
  });
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const navigate = useNavigate();
  async function handleSubmit() {
    if (!fields.vdl_contactprincipal.value || !fields.vdl_typededemande.value)
      return;
    mutation.mutate({
      title: fields.title.value,
      _vdl_contactprincipal_value: fields.vdl_contactprincipal.value,
      _vdl_typededemande_value: fields.vdl_typededemande.value,
      _parentcaseid_value: demand.incidentid,
    });
  }

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      maxWidth="md"
      fullWidth
    >
      <Box padding="16px 16px 0 16px">
        <Typography variant="h5" color="primary" sx={{ paddingBottom: "20px" }}>
          Créer une sous-demande
        </Typography>
        <Typography>
          Vous serez redirigé vers la page de détails de la demande pour compléter les informations spécifiques.
        </Typography>
      </Box>
      <SimpleFormWithSectionsWithState
        fields={fields}
        setFields={setFields}
        isFormLoading={mutation.isPending}
        columns={{ minWidth: 220, count: 1 }}
        submitText={"Initier la création"}
        handleSuccess={handleSubmit}
        cancelText="Annuler"
        handleCancel={() => setOpen(false)}
      />
    </Dialog>
  );
};

export default NewDemandDialog;
